import {
  Box,
  Button,
  MenuItem,
  Select,
  Typography,
} from "@avenue-8/sidekick-ui";
import { useCallback, useEffect, useMemo, useState } from "react";
import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";
import { ChatWidgetContainer, ChatWidgetContainerStack } from "../styles";
import { ICmaDetailsWidget } from "./types/CmaDetailsWidget";
import { usePresentationLogic } from "./components/usePresentationLogic";
import { PresentationEditor } from "./components/PresentationEditor";
import { usePdfGenerationToast } from "./components/usePdfGenerationToast";
import BrokenImageIcon from "@mui/icons-material/BrokenImage";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import PrintIcon from "@mui/icons-material/Print";
import { PhotoGalleryWrapper } from "./components/PhotoGalleryWrapper";
import { ChatWidgetNavbar, ChatWidgetNavbarAction } from "../ChatWidgetNavbar";
import { IChatWidgetProps } from "../types/ChatWidget";
import { SyncIndicator } from "@cma/shared/components/SyncIndicator";
import { PresentationSkeleton } from "@cma/shared/components/PresentationSkeleton";
import { FindListingsSearchWidgetV2 } from "../find-listings-search/FindListingsSearchWidgetV2";
import { getInitialFindListingsInputFromCma } from "../find-listings-search/helpers/search-comparable-form-model";
import { useGeneratePresentationPdf } from "@cma/shared/queries/cma/useGeneratePresentationPdf";
import { PresentationThemeProvider } from "@cma/shared/theme/PresentationThemeProvider";
import {
  DEFAULT_PRESENTATION_THEME,
  getThemesForPlan,
  presentationThemesByPlan,
} from "@cma/shared/theme/presentation-themes";
import { useAuth } from "@shared/hooks/useAuth";
import { usePresentationThemePreference } from "@cma/shared/hooks/usePresentationThemePreference";

const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const ThemeTransitionOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: ${(p) => p.theme.colors.background};
  animation: ${fadeOut} 500ms ease forwards;
  pointer-events: none;
`;

type CmaDetailsWidgetProps = IChatWidgetProps<ICmaDetailsWidget>;

export const CmaDetailsWidget = (props: CmaDetailsWidgetProps) => {
  const { widget, setChatWidget, setChatWidgetContext } = props;
  const [temporaryFindListingsWidget, setTemporaryFindListingsWidgetOpen] =
    useState<boolean>(false);

  const { isAdmin, user } = useAuth();
  const planThemes = isAdmin
    ? Object.values(presentationThemesByPlan).flat()
    : getThemesForPlan(user?.subscriptionPlanId);

  const {
    presentation,
    presentationView,
    error,
    isLoading,
    isSynching,
    isFetchingAllSectionsData,
    addSectionActions,
    updateSection,
    deleteSection,
    reorderSections,
    refetchPresentation,
    // fetchAllSectionsData,
    handleRemoveComparableProperty,
    handleUpdateComparableProperty,
    handleReorderComparableProperties,
    updatePresentationTheme,
  } = usePresentationLogic({
    cmaId: widget.content.cmaId,
  });

  const { preferredThemeId, saveThemePreference } =
    usePresentationThemePreference({
      presentationId: presentation?.id,
      presentationThemeId: presentation?.themeId,
      allowedThemeIds: planThemes.map((t) => t.id),
      onUpdatePresentationThemeId: (themeId) =>
        updatePresentationTheme({ themeId }),
    });

  const themeId =
    presentation?.themeId ?? preferredThemeId ?? DEFAULT_PRESENTATION_THEME.id;

  const featuredProperty = presentation?.sourceData.featuredProperty;

  const initialFindComparablePropertiesInput = useMemo(() => {
    if (!featuredProperty) {
      return null;
    }
    return getInitialFindListingsInputFromCma(featuredProperty);
  }, [featuredProperty]);

  const handleThemeChange = useCallback(
    (newThemeId: string) => {
      updatePresentationTheme({
        themeId: newThemeId,
        onSuccess: () => saveThemePreference(newThemeId),
      });
    },
    [updatePresentationTheme, saveThemePreference],
  );

  const isPresentationReady =
    !isLoading && presentationView && presentation && !error;

  useEffect(() => {
    if (isPresentationReady && temporaryFindListingsWidget) return;

    setChatWidgetContext({
      content: {
        ui: {
          title:
            presentation?.sourceData.presentationName || "CMA Presentation",
        },
        data: {
          cmaId: widget.content.cmaId,
        },
        instructions:
          "A CMA presentation is open in the canvas. Always fetch its details using this cmaId before responding. If you don't have the details yet, fetch them first.",
      },
    });
  }, [
    widget,
    presentation,
    setChatWidgetContext,
    temporaryFindListingsWidget,
    isPresentationReady,
  ]);

  const onOpenFindComparablePropertiesWidget = useCallback(() => {
    setTemporaryFindListingsWidgetOpen(true);
  }, []);

  const onCloseFindComparablePropertiesWidget = useCallback(() => {
    setTemporaryFindListingsWidgetOpen(false);
  }, []);

  const { mutateAsync: generatePresentationPdf, isPending: isGeneratingPdf } =
    useGeneratePresentationPdf();
  const { run: runPdfGenerationToast } = usePdfGenerationToast();

  const syncIndicatorComponent = useMemo(
    () => (
      <SyncIndicator
        isSynching={isSynching}
        updatedAt={presentation?.sourceData.updatedAt}
      />
    ),
    [isSynching, presentation?.sourceData.updatedAt],
  );

  const handleGeneratePresentationPdf = useCallback(async () => {
    if (!presentation?.id) return;
    try {
      const response = await runPdfGenerationToast(() =>
        generatePresentationPdf({
          presentationId: presentation.id,
          isAttachment: true,
        }),
      );
      window.location.href = response.url;
    } catch (error) {
      console.error("Failed to generate PDF:", error);
    }
  }, [presentation?.id, generatePresentationPdf, runPdfGenerationToast]);

  const chatWidgetNavbarActions = useMemo(() => {
    const actions: ChatWidgetNavbarAction[] = [];

    actions.push({
      key: "sync",
      icon: syncIndicatorComponent,
      label: "Sync Status",
      readOnly: true,
      grouped: false,
    });

    actions.push({
      key: "view-presentation",
      icon: <OpenInNewIcon fontSize="small" />,
      label: "Preview",
      onClick: () => {
        window.open(`/presentations/${presentation?.id}/view`, "_blank");
      },
      disabled: !isPresentationReady,
      grouped: true,
    });

    actions.push({
      key: "print-presentation",
      icon: <PrintIcon fontSize="small" />,
      label: "Print",
      onClick: handleGeneratePresentationPdf,
      disabled: !isPresentationReady || isGeneratingPdf,
      grouped: true,
    });

    if (planThemes.length > 0) {
      actions.push({
        key: "theme-selector",
        label: "Template",
        grouped: true,
        component: (closeMenu) => (
          <MenuItem disableRipple sx={{ gap: 1, minWidth: 180 }}>
            <PaletteOutlinedIcon fontSize="small" />
            <Typography variant="overline" fontWeight={700} sx={{ flex: 1 }}>
              Template
            </Typography>
            <Select
              value={themeId}
              onChange={(e) => {
                handleThemeChange(e.target.value);
                closeMenu();
              }}
              onClick={(e) => e.stopPropagation()}
              size="small"
              variant="standard"
              disableUnderline
            >
              <MenuItem value={DEFAULT_PRESENTATION_THEME.id}>
                {DEFAULT_PRESENTATION_THEME.label}
              </MenuItem>
              {planThemes.map((theme) => (
                <MenuItem key={theme.id} value={theme.id}>
                  {theme.label}
                </MenuItem>
              ))}
            </Select>
          </MenuItem>
        ),
      });
    }

    return actions;
  }, [
    syncIndicatorComponent,
    presentation?.id,
    isPresentationReady,
    handleGeneratePresentationPdf,
    isGeneratingPdf,
    themeId,
    planThemes,
    handleThemeChange,
  ]);

  return (
    <ChatWidgetContainer>
      {isPresentationReady && temporaryFindListingsWidget && (
        <ChatWidgetContainerStack>
          <FindListingsSearchWidgetV2
            widget={{
              kind: "widget-listings-search",
              temporary: true,
              artifactId: widget.artifactId ?? "",
              content: initialFindComparablePropertiesInput,
              title: "Add Comparable Property",
              subtitle:
                "Search for listings in the MLS database and select the property you wish to add",
              cmaId: widget.content.cmaId,
            }}
            setChatWidget={setChatWidget}
            setChatWidgetContext={setChatWidgetContext}
            onClose={onCloseFindComparablePropertiesWidget}
          />
        </ChatWidgetContainerStack>
      )}

      {!temporaryFindListingsWidget && (
        <ChatWidgetNavbar actions={chatWidgetNavbarActions}>
          <Typography variant="subtitle1">CMA Presentation</Typography>
        </ChatWidgetNavbar>
      )}

      <Box sx={{ opacity: temporaryFindListingsWidget ? 0 : 1 }}>
        {isLoading && <PresentationSkeleton />}

        {isPresentationReady && (
          <Box sx={{ position: "relative" }}>
            <PresentationThemeProvider themeId={themeId}>
              <PresentationEditor
                displayMode="desktop"
                presentation={presentation!}
                sections={presentationView!.sections}
                galleries={presentationView!.galleries}
                updatedAt={presentationView!.updatedAt || new Date()}
                addSectionActions={addSectionActions}
                onUpdateSection={updateSection}
                onDeleteSection={deleteSection}
                onReorderSections={reorderSections}
                onOpenFindComparablePropertiesWidget={
                  onOpenFindComparablePropertiesWidget
                }
                // onFetchSectionsData={fetchAllSectionsData}
                isFetchingAllSectionsData={isFetchingAllSectionsData}
                handleRemoveComparableProperty={handleRemoveComparableProperty}
                handleUpdateComparableProperty={handleUpdateComparableProperty}
                handleReorderComparableProperties={
                  handleReorderComparableProperties
                }
              />
              <ThemeTransitionOverlay key={themeId} />
            </PresentationThemeProvider>
          </Box>
        )}

        {error && (
          <Box
            px={2}
            py={4}
            display={"flex"}
            flexDirection="column"
            alignItems="center"
            gap={1}
          >
            <BrokenImageIcon fontSize="large" />
            <Typography variant="h6">Ops... something went wrong</Typography>
            <Typography px={6} textAlign={"center"} mb={2}>
              Error loading CMA presentation data. If the problem persists,
              please contact support.
            </Typography>
            <Button
              size="small"
              onClick={() => refetchPresentation()}
              variant="outlined"
            >
              Retry
            </Button>
          </Box>
        )}
      </Box>
      <PhotoGalleryWrapper />
    </ChatWidgetContainer>
  );
};
